package com.evoapps.evoshift.domain.specialdays

import com.evoapps.evoshift.domain.model.Voivodeship
import kotlinx.datetime.LocalDate
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/**
 * SH-227: a country's school breaks as shipped with the app (domain/data/special-days/{COUNTRY}.json,
 * copied into [BundledSpecialDays] by scripts/special_days/bundle_special_days.py). Each school
 * year names its source and when it was checked; a year not listed has no breaks shown.
 */
@Serializable
data class SchoolBreaksData(
    val country: String,
    val schemaVersion: Int = 1,
    /** The data's version (the date of its last edit). */
    val version: String = "",
    val checkedOn: String = "",
    val note: String = "",
    val sources: List<SchoolBreakSource> = emptyList(),
    val schoolYears: List<SchoolYearBreaks> = emptyList()
) {
    companion object {
        private val json = Json { ignoreUnknownKeys = true }
        fun parse(text: String): SchoolBreaksData = json.decodeFromString(serializer(), text)
    }
}

@Serializable
data class SchoolBreakSource(val id: String, val title: String, val url: String)

@Serializable
data class SchoolYearBreaks(
    /** "2025/2026". */
    val schoolYear: String,
    val sourceUrl: String = "",
    val checkedOn: String = "",
    val winterBreaks: List<WinterBreakTurn> = emptyList(),
    val summerHolidays: DateRangeData? = null
)

/** One turn of the winter break and the voivodeships (Voivodeship names) that have it. */
@Serializable
data class WinterBreakTurn(val from: String, val to: String, val voivodeships: List<String>) {
    val range: ClosedRange<LocalDate>? get() = rangeOf(from, to)
}

/**
 * A period. [basis] PUBLISHED: the ministry's calendar for that year; RULE: worked out from the
 * regulation (§ 2 ust. 1: classes end on the first Friday after 20 June) before it is published.
 */
@Serializable
data class DateRangeData(val from: String, val to: String, val basis: String = "PUBLISHED") {
    val range: ClosedRange<LocalDate>? get() = rangeOf(from, to)
}

private fun rangeOf(from: String, to: String): ClosedRange<LocalDate>? {
    val start = runCatching { LocalDate.parse(from) }.getOrNull() ?: return null
    val end = runCatching { LocalDate.parse(to) }.getOrNull() ?: return null
    return if (end < start) null else start..end
}

/** The school breaks shipped with the app. */
object SchoolBreaks {
    private val parsed: Map<String, SchoolBreaksData> by lazy {
        BundledSpecialDays.json.mapValues { (_, text) -> SchoolBreaksData.parse(text) }
    }

    /** Every country's data, by country code. */
    val all: Map<String, SchoolBreaksData> get() = parsed

    fun of(country: String): SchoolBreaksData? = parsed[country]

    /** [voivodeship]'s winter breaks (Poland) that overlap [range]. */
    fun winterBreaks(voivodeship: Voivodeship, range: ClosedRange<LocalDate>): List<ClosedRange<LocalDate>> =
        of("PL")?.schoolYears.orEmpty().flatMap { it.winterBreaks }
            .filter { voivodeship.name in it.voivodeships }
            .mapNotNull { it.range }
            .filter { it.overlaps(range) }

    /** The summer holidays (Poland) that overlap [range]. */
    fun summerHolidays(range: ClosedRange<LocalDate>): List<ClosedRange<LocalDate>> =
        of("PL")?.schoolYears.orEmpty().mapNotNull { it.summerHolidays?.range }.filter { it.overlaps(range) }
}

internal fun ClosedRange<LocalDate>.overlaps(other: ClosedRange<LocalDate>): Boolean =
    start <= other.endInclusive && endInclusive >= other.start
